import { useState, useEffect } from 'react';
import { useTasks, ASSIGN_ALL } from './TasksContext';
import api from './api';
import { useFilePreview, FilePreviewModal } from './FilePreview';

// ─── Documents (read-only tree with download links) ─────────────────────────

const docFileIcon = (name = '') => {
  const ext = name.split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','webp'].includes(ext)) return '🖼️';
  if (ext === 'pdf') return '📄';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx'].includes(ext)) return '📊';
  return '📎';
};

const countDocs = folders => (folders || []).reduce(
  (s, f) => s + (f.files?.length || 0) + (f.linkedDocuments?.length || 0) + countDocs(f.subfolders), 0
);

// Чи є в дереві хоч одна Crew-підпапка (персональна папка фрілансера) —
// такі показуємо в перегляді завжди, навіть порожні (це "куди" фрілансер
// вантажить документи, адміну треба бачити, що папка вже створена, а не
// лише коли там щось зʼявиться).
const hasCrewSubfolders = folders =>
  (folders || []).some(f => f.id === 'crew' && (f.subfolders || []).length > 0);

function DocsTree({ folders, projectId, depth = 0 }) {
  const { previewFile, openPreview, closePreview } = useFilePreview();
  // Розгорнуто/згорнуто — суто локальний UI-стан (не зберігається на
  // бекенді, скидається при перезавантаженні сторінки). За замовчуванням усі
  // папки розгорнуті, як і раніше.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggle = id => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (folders || []).map(f => {
    const linked = f.linkedDocuments || [];
    const total = (f.files?.length || 0) + linked.length + countDocs(f.subfolders);
    const isCrewRoot   = f.id === 'crew';
    const isCrewMember = f.type === 'crew_member';
    // Папки, які фрілансер створив сам (через свій кабінет) — теж завжди
    // показуємо, навіть порожні: інакше щойно створена папка "зникає" з
    // цього перегляду, поки в неї не завантажили файл (адмін бачить її лише
    // у формі редагування, де такого фільтра нема — звідси й розбіжність).
    const isCustom     = f.type === 'custom' && !!f.createdByFreelancerId;
    // Порожні папки ховаємо з перегляду — КРІМ Crew-кореня, персональних
    // підпапок фрілансерів (isCrewMember) і папок, створених фрілансером.
    if (total === 0 && !isCrewRoot && !isCrewMember && !isCustom) return null;
    const isOpen = !collapsed.has(f.id);
    return (
      <div key={f.id} style={{ marginLeft: depth ? '16px' : 0, marginBottom: '6px' }}>
        <div
          onClick={() => toggle(f.id)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-body)', fontSize: '13px', fontWeight: 'bold', padding: '4px 0', cursor: 'pointer', userSelect: 'none' }}>
          <span style={{ fontSize: '10px', color: 'var(--text-dim)', width: '10px' }}>{isOpen ? '▼' : '▶'}</span>
          📁 {f.name}{total > 0 && <span style={{ color: 'var(--text-disabled)', fontWeight: 'normal', fontSize: '11px' }}> ({total})</span>}
        </div>
        {isOpen && (
          <>
            {/* Профільні документи фрілансера (паспорт/фото/CV/інше) — живе
                посилання на freelancer_documents, не копія в project_files, тому
                окремий download-роут через api.freelancers.documentUrl. */}
            {linked.map(doc => (
              <div key={`linked-${doc.id}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0 3px 20px' }}>
                <span style={{ fontSize: '13px' }}>{docFileIcon(doc.name)}</span>
                <span
                  onClick={() => openPreview({
                    url: api.freelancers.documentUrl(f.freelancerId, doc.id),
                    downloadUrl: api.freelancers.documentUrl(f.freelancerId, doc.id, { download: true }),
                    name: doc.name,
                  })}
                  style={{ color: 'var(--info-text)', fontSize: '12px', cursor: 'pointer' }}>
                  {doc.name}
                </span>
                <span style={{ color: 'var(--text-disabled)', fontSize: '10px', textTransform: 'uppercase' }}>{doc.category}</span>
                <span style={{ color: 'var(--text-disabled)', fontSize: '10px', marginLeft: 'auto' }}>From profile</span>
              </div>
            ))}
            {(f.files || []).map(file => (
              <div key={file.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0 3px 20px' }}>
                <span style={{ fontSize: '13px' }}>{docFileIcon(file.name)}</span>
                {file.serverId && projectId ? (
                  <span
                    onClick={() => openPreview({
                      url: api.projects.fileUrl(projectId, file.serverId),
                      downloadUrl: api.projects.fileUrl(projectId, file.serverId, { download: true }),
                      name: file.name,
                    })}
                    style={{ color: 'var(--info-text)', fontSize: '12px', cursor: 'pointer' }}>
                    {file.name}
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{file.name}</span>
                )}
                <span style={{ color: 'var(--text-disabled)', fontSize: '10px' }}>{file.size}</span>
              </div>
            ))}
            {total === 0 && isCrewMember && (
              <p style={{ color: 'var(--text-disabled)', fontSize: '11px', margin: '2px 0 0 20px' }}>No documents yet.</p>
            )}
            <DocsTree folders={f.subfolders} projectId={projectId} depth={depth + 1} />
          </>
        )}
        <FilePreviewModal file={previewFile} onClose={closePreview} />
      </div>
    );
  });
}

const statusConfig = {
  'Planning':   { color: 'var(--info-text)', bg: 'var(--info-bg)' },
  'Confirmed':  { color: 'var(--purple-text)', bg: 'var(--purple-bg)' },
  'Active':     { color: 'var(--success-text)', bg: 'var(--success-bg)' },
  'On Hold':    { color: 'var(--warning-text)', bg: 'var(--warning-bg)' },
  'Completed':  { color: 'var(--text-muted)',    bg: 'var(--bg-subtle)' },
  'Canceled':   { color: 'var(--accent)', bg: 'var(--danger-bg)' },
};

// Готовність фрілансера (project_assignment_status.confirmation_status) —
// НЕ відображений статус фрілансера (той дзеркалить статус проєкту, див.
// StatusBadge/crewGroups нижче), а окрема мітка для адміна: чи фрілансер уже
// підтвердив, що готовий до проєкту, поки той у Planning/On Hold.
const CONFIRMATION_CONFIG = {
  pending:   { label: 'Awaiting readiness', color: 'var(--warning-text)', bg: 'var(--warning-bg)' },
  confirmed: { label: 'Ready',              color: 'var(--success-text)', bg: 'var(--success-bg)' },
  declined:  { label: 'Declined project',   color: 'var(--accent)',       bg: 'var(--danger-bg)' },
};

const PRIORITY_CONFIG = {
  Low:    { color: 'var(--text-dim)'    },
  Medium: { color: 'var(--warning-text)' },
  High:   { color: 'var(--accent)' },
  Urgent: { color: '#ff3860' },
};

const MOCK_ACTIVITY = [];

// ─── Finance (read-only) ────────────────────────────────────────────────────
// Дзеркалить обчислення й структуру CrewingFinance/DryHireFinance/
// PermanentInstallationFinance з ProjectForm.js — той самий шлях полів
// (income.projectPrice.amount, freelancerPayments.paid, тощо), але без
// інпутів, лише для перегляду. project.finance = { crewing, dryHire, permInstall },
// один з трьох ключів заповнений залежно від project.type (див. api.js
// normalizeProject — фінанси лежать у finance_extra.finance).

const n           = v   => parseFloat(v) || 0;
const sumFines    = arr => (arr || []).reduce((s, f) => s + n(f.amount), 0);
const sumPayments = arr => (arr || []).reduce((s, p) => s + n(p.amount), 0);

function FinanceDivider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0 14px' }}>
      <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
      <span style={{ color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
    </div>
  );
}

function ReadTotalRow({ label, value, cur, color }) {
  const fmt = v => `${cur} ${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 16px', margin: '6px 0 12px' }}>
      <span style={{ color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</span>
      <span style={{ color: color || 'var(--info-text)', fontSize: '17px', fontWeight: 'bold' }}>{fmt(value)}</span>
    </div>
  );
}

function ReadAmountRow({ label, value, comment, cur, hint }) {
  if (!n(value) && !comment) return null; // порожні поля не захаращують перегляд
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid var(--bg-subtle)' }}>
      <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>
        {label}{hint && <span style={{ color: 'var(--text-disabled)', fontStyle: 'italic', marginLeft: '6px' }}>{hint}</span>}
        {comment && <div style={{ color: 'var(--text-disabled)', fontSize: '11px', marginTop: '2px', maxWidth: '220px' }}>💬 {comment}</div>}
      </span>
      <span style={{ color: 'var(--text-body)', fontSize: '13px', flexShrink: 0 }}>{cur} {n(value).toLocaleString()}</span>
    </div>
  );
}

function ReadFinesList({ fines, cur }) {
  const list = (fines || []).filter(f => n(f.amount) || f.comment);
  if (list.length === 0) return null;
  return (
    <div style={{ marginBottom: '4px' }}>
      {list.map((f, i) => (
        <div key={f.id ?? i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid var(--bg-subtle)' }}>
          <span style={{ color: 'var(--danger-text-soft)', fontSize: '12px' }}>
            ⚠ Fine #{i + 1}
            {f.comment && <div style={{ color: 'var(--text-disabled)', fontSize: '11px', marginTop: '2px', maxWidth: '220px' }}>💬 {f.comment}</div>}
          </span>
          <span style={{ color: 'var(--accent)', fontSize: '13px', flexShrink: 0 }}>− {cur} {n(f.amount).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function ReadPaymentsList({ payments, cur, label }) {
  const list = payments || [];
  const total = sumPayments(list);
  return (
    <div style={{ marginBottom: '4px' }}>
      {label && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px 0 6px' }}>
          <span style={{ color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</span>
          {total > 0 && <span style={{ color: 'var(--success-text)', fontSize: '12px', fontWeight: 'bold' }}>{cur} {total.toLocaleString()} paid</span>}
        </div>
      )}
      {list.length === 0 ? (
        <p style={{ color: 'var(--text-disabled)', fontSize: '12px', margin: '4px 0' }}>No payments recorded</p>
      ) : list.map((p, i) => (
        <div key={p.id ?? i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid var(--bg-subtle)' }}>
          <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>
            Payment #{i + 1}{p.date ? ` — ${p.date}` : ''}
            {p.comment && <div style={{ color: 'var(--text-disabled)', fontSize: '11px', marginTop: '2px', maxWidth: '220px' }}>💬 {p.comment}</div>}
          </span>
          <span style={{ color: 'var(--success-text)', fontSize: '13px', flexShrink: 0 }}>{cur} {n(p.amount).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function CrewingFinanceView({ data, cur }) {
  const i  = data?.income || {};
  const fp = data?.freelancerPayments || {};

  const totalPrice      = n(i.projectPrice?.amount) + n(i.crewExpenses?.amount) + n(i.crewOvertimes?.amount) - sumFines(i.fines);
  const clientPaid      = sumPayments(data?.clientPayments);
  const clientToBePaid  = totalPrice - clientPaid;
  const totalFreelancer = n(fp.planned?.amount) + n(fp.crewExpenses?.amount) + n(fp.crewOvertimes?.amount) - sumFines(fp.fines);
  const freelancerPaid  = sumPayments(fp.paid);
  const freelancerToBe  = totalFreelancer - freelancerPaid;
  const profit          = totalPrice - totalFreelancer;

  return (
    <>
      <FinanceDivider label="Income" />
      <ReadAmountRow label="Project Price"  value={i.projectPrice?.amount}  comment={i.projectPrice?.comment}  cur={cur} />
      <ReadAmountRow label="Crew Expenses"  value={i.crewExpenses?.amount}  comment={i.crewExpenses?.comment}  cur={cur} />
      <ReadAmountRow label="Crew Overtimes" value={i.crewOvertimes?.amount} comment={i.crewOvertimes?.comment} cur={cur} />
      <ReadFinesList fines={i.fines} cur={cur} />
      <ReadTotalRow label="Total Price" value={totalPrice} cur={cur} color="var(--info-text)" />

      <FinanceDivider label="Payments from Client" />
      <ReadPaymentsList payments={data?.clientPayments} cur={cur} />
      <ReadTotalRow label="To be Paid by Client" value={clientToBePaid} cur={cur}
        color={clientToBePaid <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      <FinanceDivider label="Payments to Freelancers" />
      <ReadAmountRow label="Planned Payment for Freelancers" value={fp.planned?.amount} comment={fp.planned?.comment} cur={cur} />
      <ReadAmountRow label="Crew Expenses"  value={fp.crewExpenses?.amount}  comment={fp.crewExpenses?.comment}  cur={cur} />
      <ReadAmountRow label="Crew Overtimes" value={fp.crewOvertimes?.amount} comment={fp.crewOvertimes?.comment} cur={cur} />
      <ReadFinesList fines={fp.fines} cur={cur} />
      <ReadTotalRow label="Total Payments to Freelancers" value={totalFreelancer} cur={cur} color="var(--warning-text)" />
      <ReadPaymentsList payments={fp.paid} cur={cur} label="Already Paid to Freelancers" />
      <ReadTotalRow label="To be Paid to Freelancers" value={freelancerToBe} cur={cur}
        color={freelancerToBe <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      <FinanceDivider label="Profit" />
      <ReadTotalRow label="PROFIT" value={profit} cur={cur} color={profit >= 0 ? 'var(--success-text)' : 'var(--accent)'} />
    </>
  );
}

function DryHireFinanceView({ data, cur }) {
  const i  = data?.income || {};
  const sp = data?.supplierPayments || {};

  const totalPrice     = n(i.projectPrice?.amount) + n(i.projectExpenses?.amount) + n(i.paymentForDamages?.amount) - sumFines(i.fines);
  const clientPaid     = sumPayments(data?.clientPayments);
  const clientToBePaid = totalPrice - clientPaid;
  const totalSupplier  = n(sp.planned?.amount) + n(sp.paymentForDamages?.amount) - sumFines(sp.fines);
  const supplierPaid   = sumPayments(sp.paid);
  const supplierToBe   = totalSupplier - supplierPaid;
  const profit         = totalPrice - totalSupplier;

  return (
    <>
      <FinanceDivider label="Income" />
      <ReadAmountRow label="Project Price"      value={i.projectPrice?.amount}      comment={i.projectPrice?.comment}      cur={cur} />
      <ReadAmountRow label="Project Expenses"   value={i.projectExpenses?.amount}   comment={i.projectExpenses?.comment}   cur={cur} />
      <ReadAmountRow label="Payment for Damages" value={i.paymentForDamages?.amount} comment={i.paymentForDamages?.comment} cur={cur} />
      <ReadFinesList fines={i.fines} cur={cur} />
      <ReadTotalRow label="Total Price" value={totalPrice} cur={cur} color="var(--info-text)" />

      <FinanceDivider label="Payments from Client" />
      <ReadPaymentsList payments={data?.clientPayments} cur={cur} />
      <ReadTotalRow label="To be Paid by Client" value={clientToBePaid} cur={cur}
        color={clientToBePaid <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      <FinanceDivider label="Payments to Suppliers" />
      <ReadAmountRow label="Planned Payment for Suppliers" value={sp.planned?.amount} comment={sp.planned?.comment} cur={cur} />
      <ReadFinesList fines={sp.fines} cur={cur} />
      <ReadAmountRow label="Payment for Damages" value={sp.paymentForDamages?.amount} comment={sp.paymentForDamages?.comment} cur={cur} />
      <ReadTotalRow label="Total Payments to Suppliers" value={totalSupplier} cur={cur} color="var(--warning-text)" />
      <ReadPaymentsList payments={sp.paid} cur={cur} label="Already Paid to Suppliers" />
      <ReadTotalRow label="To be Paid to Suppliers" value={supplierToBe} cur={cur}
        color={supplierToBe <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      <FinanceDivider label="Profit" />
      <ReadTotalRow label="PROFIT" value={profit} cur={cur} color={profit >= 0 ? 'var(--success-text)' : 'var(--accent)'} />
    </>
  );
}

function PermanentInstallationFinanceView({ data, cur }) {
  const i  = data?.income || {};
  const sp = data?.supplierPayments || {};
  const fp = data?.freelancerPayments || {};

  const totalPrice     = n(i.installationCost?.amount) + n(i.equipmentCost?.amount) - sumFines(i.fines);
  const clientPaid     = sumPayments(data?.clientPayments);
  const clientToBePaid = totalPrice - clientPaid;

  const totalSupplier  = n(sp.planned?.amount) + n(sp.paymentForDamages?.amount) - sumFines(sp.fines);
  const supplierPaid   = sumPayments(sp.paid);
  const supplierToBe   = totalSupplier - supplierPaid;

  const totalFreelancer = n(fp.planned?.amount) - sumFines(fp.fines);
  const freelancerPaid  = sumPayments(fp.paid);
  const freelancerToBe  = totalFreelancer - freelancerPaid;

  const profit = totalPrice - totalSupplier - totalFreelancer;

  return (
    <>
      <FinanceDivider label="Income" />
      <ReadAmountRow label="Installation Cost" value={i.installationCost?.amount} comment={i.installationCost?.comment} cur={cur} />
      <ReadAmountRow label="Equipment Cost"    value={i.equipmentCost?.amount}    comment={i.equipmentCost?.comment}    cur={cur} />
      <ReadFinesList fines={i.fines} cur={cur} />
      <ReadTotalRow label="Total Price" value={totalPrice} cur={cur} color="var(--info-text)" />

      <FinanceDivider label="Payments from Client" />
      <ReadPaymentsList payments={data?.clientPayments} cur={cur} />
      <ReadTotalRow label="To be Paid by Client" value={clientToBePaid} cur={cur}
        color={clientToBePaid <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      <FinanceDivider label="Payments to Suppliers (Equipment)" />
      <ReadAmountRow label="Planned Payment for Suppliers" value={sp.planned?.amount} comment={sp.planned?.comment} cur={cur} />
      <ReadAmountRow label="Payment for Damages" value={sp.paymentForDamages?.amount} comment={sp.paymentForDamages?.comment} cur={cur} />
      <ReadFinesList fines={sp.fines} cur={cur} />
      <ReadTotalRow label="Total Payments to Suppliers" value={totalSupplier} cur={cur} color="var(--warning-text)" />
      <ReadPaymentsList payments={sp.paid} cur={cur} label="Already Paid to Suppliers" />
      <ReadTotalRow label="To be Paid to Suppliers" value={supplierToBe} cur={cur}
        color={supplierToBe <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      <FinanceDivider label="Payments to Freelancers (Installation)" />
      <ReadAmountRow label="Planned Payment for Freelancers" value={fp.planned?.amount} comment={fp.planned?.comment} cur={cur} />
      <ReadFinesList fines={fp.fines} cur={cur} />
      <ReadTotalRow label="Total Payments to Freelancers" value={totalFreelancer} cur={cur} color="var(--warning-text)" />
      <ReadPaymentsList payments={fp.paid} cur={cur} label="Already Paid to Freelancers" />
      <ReadTotalRow label="To be Paid to Freelancers" value={freelancerToBe} cur={cur}
        color={freelancerToBe <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      <FinanceDivider label="Profit" />
      <div style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 18px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-dim)', marginBottom: '6px' }}>
          <span>Total Price</span><span style={{ color: 'var(--info-text)' }}>{cur} {totalPrice.toLocaleString()}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-dim)', marginBottom: '6px' }}>
          <span>− Suppliers</span><span style={{ color: 'var(--warning-text)' }}>{cur} {totalSupplier.toLocaleString()}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-dim)', marginBottom: '10px' }}>
          <span>− Freelancers</span><span style={{ color: 'var(--warning-text)' }}>{cur} {totalFreelancer.toLocaleString()}</span>
        </div>
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>PROFIT</span>
          <span style={{ color: profit >= 0 ? 'var(--success-text)' : 'var(--accent)', fontSize: '20px', fontWeight: 'bold' }}>{cur} {profit.toLocaleString()}</span>
        </div>
      </div>
    </>
  );
}

function FinanceBreakdown({ project }) {
  const cur = project.currency || 'EUR';
  if (project.type === 'Crewing')                return <CrewingFinanceView              data={project.finance?.crewing}     cur={cur} />;
  if (project.type === 'Dry hire')               return <DryHireFinanceView               data={project.finance?.dryHire}     cur={cur} />;
  if (project.type === 'Permanent installation') return <PermanentInstallationFinanceView data={project.finance?.permInstall} cur={cur} />;
  return <p style={{ color: 'var(--text-disabled)', fontSize: '13px', margin: 0 }}>No finance data — project type not set.</p>;
}

// ─── Client's Responsible Persons (read-only) ───────────────────────────────

function ResponsiblePersonsView({ persons = [] }) {
  const list = persons.filter(p => p && (p.name || p.role || p.phone || p.email));
  if (list.length === 0) return <p style={{ color: 'var(--text-disabled)', fontSize: '13px', margin: 0 }}>No responsible persons added</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {list.map((p, i) => (
        <div key={i} style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 'bold' }}>{p.name || '—'}</span>
            {p.visibleToFreelancers && (
              <span style={{ background: 'var(--info-bg)', color: 'var(--info-text)', border: '1px solid var(--info-text)', borderRadius: '4px', padding: '1px 6px', fontSize: '10px' }}>
                Visible to freelancers
              </span>
            )}
          </div>
          {p.role && <div style={{ color: 'var(--text-dim)', fontSize: '12px', marginTop: '2px' }}>{p.role}</div>}
          {(p.phone || p.email) && (
            <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
              {[p.phone, p.email].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Equipment (read-only, Dry hire) ────────────────────────────────────────

function EquipmentView({ items = [] }) {
  if (items.length === 0) return <p style={{ color: 'var(--text-disabled)', fontSize: '13px', margin: 0 }}>No equipment assigned</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--bg-subtle)' }}>
          <div>
            <span style={{ color: 'var(--text-body)', fontSize: '13px' }}>{it.name || '—'}</span>
            {it.category && <span style={{ color: 'var(--text-disabled)', fontSize: '11px', marginLeft: '8px' }}>[{it.category}]</span>}
          </div>
          <span style={{ color: 'var(--text-dim)', fontSize: '12px', flexShrink: 0 }}>{it.qty}{it.unit ? ` ${it.unit}` : ''}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || { color: 'var(--text-muted)', bg: 'var(--bg-subtle)' };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`, borderRadius: '4px', padding: '2px 10px', fontSize: '11px', fontWeight: 'bold' }}>
      {status}
    </span>
  );
}

// Окремий вимір від основного статусу — стан оплати (множинний).
const paymentStatusConfig = {
  'Waiting for Payment':  { color: 'var(--warning-text)', bg: 'var(--warning-bg)' },
  'Payment for Supplier': { color: 'var(--purple-text)', bg: 'var(--purple-bg)' },
  'Equipment Checking':   { color: 'var(--info-text)', bg: 'var(--info-bg)' },
};
function PaymentStatusBadges({ statuses = [] }) {
  if (!statuses || statuses.length === 0) return null;
  return (
    <>
      {statuses.map(s => {
        const cfg = paymentStatusConfig[s] || { color: 'var(--text-muted)', bg: 'var(--bg-subtle)' };
        return (
          <span key={s} style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`, borderRadius: '4px', padding: '2px 8px', fontSize: '10px', fontWeight: 'bold', whiteSpace: 'nowrap', marginLeft: '6px' }}>
            {s}
          </span>
        );
      })}
    </>
  );
}

function SectionCard({ title, children, action }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <p style={{ color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', margin: 0 }}>{title}</p>
        {action}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--bg-subtle)' }}>
      <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>{label}</span>
      <span style={{ color: color || 'var(--text-body)', fontSize: '13px', fontWeight: color ? 'bold' : 'normal' }}>{value || '—'}</span>
    </div>
  );
}

// ─── Timeline (Gantt) ─────────────────────────────────────────────────────────

function Timeline({ project }) {
  const YEAR = 2026;
  const yearStart = new Date(`${YEAR}-01-01`);
  const yearEnd   = new Date(`${YEAR}-12-31`);
  const totalMs   = yearEnd - yearStart;

  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(YEAR, i, 1).toLocaleString('en', { month: 'short' })
  );

  const bar = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    if (e < yearStart || s > yearEnd) return null;
    const left  = Math.max(0, (s - yearStart) / totalMs * 100);
    const width = Math.min(100 - left, (Math.min(e, yearEnd) - Math.max(s, yearStart)) / totalMs * 100);
    return { left, width };
  };

  const projectBar = bar(project.startDate, project.endDate);
  const crewColors = ['var(--info-text)', 'var(--purple-text)', 'var(--success-text)', 'var(--warning-text)'];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', marginBottom: '8px' }}>
        {months.map(m => (
          <div key={m} style={{ color: 'var(--text-disabled)', fontSize: '10px', textAlign: 'center' }}>{m}</div>
        ))}
      </div>

      <div style={{ position: 'relative', background: 'var(--bg-input)', borderRadius: '6px', overflow: 'hidden', padding: '10px 0' }}>
        {/* Grid lines */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {months.map((m, i) => (
            <div key={i} style={{ borderRight: i < 11 ? '1px solid var(--border)' : 'none' }} />
          ))}
        </div>

        {/* Project bar */}
        <div style={{ padding: '0 0 8px 0' }}>
          <div style={{ color: 'var(--text-dim)', fontSize: '11px', marginBottom: '4px', paddingLeft: '4px' }}>Project</div>
          <div style={{ position: 'relative', height: '28px' }}>
            {projectBar && (
              <div style={{
                position: 'absolute', left: `${projectBar.left}%`, width: `${projectBar.width}%`,
                height: '100%', background: 'var(--accent)', borderRadius: '4px',
                display: 'flex', alignItems: 'center', paddingLeft: '8px',
                fontSize: '11px', color: 'var(--text-primary)', fontWeight: 'bold',
                overflow: 'hidden', whiteSpace: 'nowrap', boxSizing: 'border-box',
              }}>
                {project.startDate} → {project.endDate}
              </div>
            )}
          </div>
        </div>

        {/* Crew bars */}
        {(project.assigned || []).length > 0 && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
            <div style={{ color: 'var(--text-dim)', fontSize: '11px', marginBottom: '4px', paddingLeft: '4px' }}>Crew</div>
            {(project.assigned || []).map((a, i) => {
              const name = typeof a === 'string' ? a : (a?.name || '');
              const b = a?.dateFrom && a?.dateTo ? bar(a.dateFrom, a.dateTo) : bar(project.startDate, project.endDate);
              const col = crewColors[i % crewColors.length];
              return (
                <div key={i} style={{ marginBottom: '6px' }}>
                  <div style={{ color: 'var(--text-disabled)', fontSize: '10px', paddingLeft: '4px', marginBottom: '2px' }}>{name}{a?.isAdmin ? ' 🛡' : ''}</div>
                  <div style={{ position: 'relative', height: '20px' }}>
                    {b && (
                      <div style={{ position: 'absolute', left: `${b.left}%`, width: `${b.width}%`, height: '100%', background: col, borderRadius: '4px', opacity: 0.7 }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ color: 'var(--border-strong)', fontSize: '11px', marginTop: '6px', textAlign: 'right' }}>{YEAR}</div>
    </div>
  );
}

// ─── Tasks ────────────────────────────────────────────────────────────────────

const PRIORITIES  = ['Low', 'Medium', 'High', 'Urgent'];
const TASK_STATUS = ['To Do', 'In Progress', 'Done'];
const STAFF_ROLES = ['Admin', 'SuperAdmin', 'PM'];

function Tasks({ project, allUsers = [] }) {
  const { getProjectTasks, addTask, updateTask, deleteTask, currentUser } = useTasks();

  const isStaff  = STAFF_ROLES.includes(currentUser?.role);
  const allTasks = getProjectTasks(project.id);
  // Staff sees all project tasks; everyone else sees only their own (or "All")
  const tasks    = isStaff ? allTasks : allTasks.filter(t => t.assignedTo === currentUser?.id || t.assignedTo === ASSIGN_ALL);

  const [adding, setAdding]           = useState(false);
  const [newText, setNewText]         = useState('');
  const [newDesc, setNewDesc]         = useState('');
  const [newPriority, setNewPriority] = useState('Medium');
  const [newAssignee, setNewAssignee] = useState(ASSIGN_ALL);
  const [newDeadline, setNewDeadline] = useState('');

  const userName = id => allUsers.find(u => u.id === id)?.name || '—';

  const handleAdd = () => {
    if (!newText.trim()) return;
    addTask({
      projectId:   project.id,
      projectName: project.name,
      title:       newText.trim(),
      description: newDesc.trim(),
      priority:    newPriority,
      assignedTo:  newAssignee,
      deadline:    newDeadline,
    });
    setNewText(''); setNewDesc(''); setNewDeadline(''); setAdding(false);
  };

  const toggleDone = task =>
    updateTask(task.id, { status: task.status === 'Done' ? 'To Do' : 'Done' });

  const changeStatus = (task, status) => updateTask(task.id, { status });

  const inputStyle = {
    background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: '6px',
    color: 'var(--text-primary)', padding: '7px 10px', fontSize: '13px', outline: 'none',
  };

  const STATUS_COLOR = { 'To Do': 'var(--text-dim)', 'In Progress': 'var(--warning-text)', 'Done': 'var(--success-text)' };

  const pending   = tasks.filter(t => t.status !== 'Done');
  const completed = tasks.filter(t => t.status === 'Done');

  const TaskRow = ({ task }) => {
    const pc = PRIORITY_CONFIG[task.priority] || { color: 'var(--text-dim)' };
    const assigneeLabel = task.assignedTo === ASSIGN_ALL ? '🌐 All Users' : (task.assignedToName || userName(task.assignedTo));
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 0', borderBottom: '1px solid var(--bg-hover)' }}>
        <input
          type="checkbox"
          checked={task.status === 'Done'}
          onChange={() => toggleDone(task)}
          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--success-text)', marginTop: '2px', flexShrink: 0 }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ color: task.status === 'Done' ? 'var(--text-disabled)' : 'var(--text-body)', fontSize: '13px', textDecoration: task.status === 'Done' ? 'line-through' : 'none', marginBottom: '4px' }}>
            {task.title}
          </div>
          {task.description && (
            <div style={{ color: 'var(--text-dim)', fontSize: '12px', marginBottom: '4px' }}>{task.description}</div>
          )}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: pc.color, fontSize: '10px', fontWeight: 'bold', border: `1px solid ${pc.color}`, borderRadius: '4px', padding: '1px 6px' }}>
              {task.priority}
            </span>
            <select
              value={task.status}
              onChange={e => changeStatus(task, e.target.value)}
              title="Change status"
              style={{ color: STATUS_COLOR[task.status], background: 'var(--bg-card)', fontSize: '10px', border: `1px solid ${STATUS_COLOR[task.status]}`, borderRadius: '4px', padding: '1px 4px', cursor: 'pointer', outline: 'none' }}>
              {TASK_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {isStaff && (
              <span style={{ color: 'var(--text-disabled)', fontSize: '11px' }}>→ {assigneeLabel}</span>
            )}
            {task.createdByName && (
              <span style={{ color: 'var(--border-strong)', fontSize: '11px' }}>by {task.createdByName}</span>
            )}
            {task.deadline && (
              <span style={{ color: task.deadline < new Date().toISOString().slice(0,10) && task.status !== 'Done' ? 'var(--accent)' : 'var(--text-disabled)', fontSize: '11px' }}>
                📅 {task.deadline}
              </span>
            )}
          </div>
        </div>
        {isStaff && (
          <span
            onClick={() => deleteTask(task.id)}
            style={{ color: 'var(--border-strong)', fontSize: '14px', cursor: 'pointer', flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--border-strong)'}>
            ✕
          </span>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Summary chips */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        {TASK_STATUS.map(s => {
          const count = tasks.filter(t => t.status === s).length;
          return (
            <span key={s} style={{ background: 'var(--bg-subtle)', border: `1px solid ${STATUS_COLOR[s]}`, color: STATUS_COLOR[s], borderRadius: '20px', padding: '2px 10px', fontSize: '11px' }}>
              {s}: {count}
            </span>
          );
        })}
      </div>

      {/* Pending */}
      {pending.length === 0 && !adding && (
        <div style={{ color: 'var(--text-disabled)', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>No pending tasks</div>
      )}
      {pending.map(t => <TaskRow key={t.id} task={t} />)}

      {/* Add form */}
      {isStaff && (
        adding ? (
          <div style={{ background: 'var(--bg-subtle)', borderRadius: '8px', padding: '14px', marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <input
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              placeholder="Task title *"
              value={newText}
              onChange={e => setNewText(e.target.value)}
              autoFocus
            />
            <input
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              placeholder="Description (optional)"
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
              <select style={inputStyle} value={newPriority} onChange={e => setNewPriority(e.target.value)}>
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
              <select style={inputStyle} value={newAssignee} onChange={e => setNewAssignee(e.target.value)}>
                <option value={ASSIGN_ALL}>🌐 All Users</option>
                {allUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                ))}
              </select>
              <input
                type="date"
                style={{ ...inputStyle }}
                value={newDeadline}
                onChange={e => setNewDeadline(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleAdd} style={{ flex: 1, background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                Create Task
              </button>
              <button onClick={() => { setAdding(false); setNewText(''); setNewDesc(''); setNewDeadline(''); }}
                style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAdding(true)}
            style={{ width: '100%', background: 'transparent', border: '1px dashed var(--border-strong)', color: 'var(--text-dim)', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', marginTop: '12px' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-strong)';    e.currentTarget.style.color = 'var(--text-dim)'; }}>
            + Create Task
          </button>
        )
      )}

      {/* Completed */}
      {completed.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <p style={{ color: 'var(--border-strong)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px 0' }}>Completed ({completed.length})</p>
          {completed.map(t => <TaskRow key={t.id} task={t} />)}
        </div>
      )}
    </div>
  );
}

// ─── Comments ─────────────────────────────────────────────────────────────────
// Реальні коментарі проєкту — зберігаються в БД (project_comments) через API.

function Comments({ project }) {
  const { currentUser } = useTasks();
  const [comments, setComments] = useState([]);
  const [draft, setDraft]       = useState('');
  const [loading, setLoading]   = useState(true);
  const [posting, setPosting]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.projects.listComments(project.id)
      .then(res => { if (!cancelled) setComments(res.comments || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [project.id]);

  const addComment = async () => {
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const { comment } = await api.projects.addComment(project.id, text);
      setComments(prev => [...prev, comment]);
      setDraft('');
    } catch (e) {
      alert('Не вдалося додати коментар: ' + e.message);
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async id => {
    try {
      await api.projects.deleteComment(project.id, id);
      setComments(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      alert('Не вдалося видалити коментар: ' + e.message);
    }
  };

  const fmtTime = iso => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toISOString().slice(0, 16).replace('T', ' ');
  };

  const canDelete = c => c.userId === currentUser?.id || ['Admin', 'PM', 'SuperAdmin'].includes(currentUser?.role);

  return (
    <div>
      {loading ? (
        <p style={{ color: 'var(--text-disabled)', fontSize: '13px', margin: 0 }}>Завантаження...</p>
      ) : (
        comments.map(c => (
          <div key={c.id} style={{ background: 'var(--bg-input)', border: '1px solid var(--bg-subtle)', borderRadius: '8px', padding: '12px 14px', marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-body)', fontSize: '12px', fontWeight: 'bold' }}>{c.userName}</span>
                <span style={{ color: 'var(--text-disabled)', fontSize: '11px' }}>{fmtTime(c.createdAt)}</span>
              </div>
              {canDelete(c) && (
                <span onClick={() => deleteComment(c.id)} style={{ color: 'var(--border-strong)', fontSize: '13px', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--border-strong)'}>✕</span>
              )}
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0, lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{c.text}</p>
          </div>
        ))
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <textarea
          style={{ flex: 1, background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: '8px', color: 'var(--text-primary)', padding: '10px 12px', fontSize: '13px', outline: 'none', resize: 'none', height: '64px', fontFamily: 'Arial, sans-serif' }}
          placeholder="Add a comment..."
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addComment(); } }}
        />
        <button onClick={addComment} disabled={posting} style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '0 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', alignSelf: 'stretch', opacity: posting ? 0.6 : 1 }}>
          Post
        </button>
      </div>
      <p style={{ color: 'var(--border-strong)', fontSize: '11px', margin: '4px 0 0 0' }}>Enter to post · Shift+Enter for new line</p>
    </div>
  );
}

// ─── Activity Log ─────────────────────────────────────────────────────────────

function ActivityLog({ project }) {
  const { getProjectTasks } = useTasks();
  const tasks = getProjectTasks(project.id);

  const taskActivity = tasks.map(t => ({
    id:     `task-${t.id}`,
    user:   t.createdByName || 'Unknown',
    action: `Task created: "${t.title}" → assigned to ${t.assignedTo === 'All' ? 'All Users' : (t.assignedToName || '—')}`,
    time:   `${t.createdAt} 08:00`,
  }));

  const crewActivity = (project.assigned || []).map((name, i) => ({
    id:     `crew-${i}`,
    user:   'Admin',
    action: `${name} assigned to project`,
    time:   `${project.startDate || '2026-06-01'} 08:00`,
  }));

  const activity = [...MOCK_ACTIVITY, ...taskActivity, ...crewActivity]
    .sort((a, b) => b.time.localeCompare(a.time));

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', left: '15px', top: 0, bottom: 0, width: '1px', background: 'var(--bg-subtle)' }} />
      {activity.map(item => (
        <div key={item.id} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '16px', position: 'relative' }}>
          <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'var(--border)', border: '1px solid var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0, zIndex: 1 }}>
            {item.user[0]}
          </div>
          <div>
            <span style={{ color: 'var(--text-body)', fontSize: '13px', fontWeight: 'bold' }}>{item.user} </span>
            <span style={{ color: 'var(--text-faint)', fontSize: '13px' }}>{item.action}</span>
            <div style={{ color: 'var(--text-disabled)', fontSize: '11px', marginTop: '2px' }}>{item.time}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function ProjectDetail({ project, onBack, onEdit, allUsers = [] }) {
  const { getProjectTasks } = useTasks();
  const allTasks   = getProjectTasks(project.id);
  const pendingCnt = allTasks.filter(t => t.status !== 'Done').length;

  // project.assigned — плоский список, той самий фрілансер може мати кілька
  // записів (проміжки участі з перервами). Групуємо по імені для показу:
  // один бейдж підтвердження на людину, під ним — усі її проміжки дат.
  const crewGroups = [];
  (project.assigned || []).forEach(a => {
    const name = typeof a === 'string' ? a : a.name;
    const isAdmin = typeof a === 'object' && a.isAdmin;
    const dateFrom = typeof a === 'object' ? a.dateFrom : null;
    const dateTo   = typeof a === 'object' ? a.dateTo   : null;
    const role     = typeof a === 'object' ? a.role     : null;
    const travelDates = typeof a === 'object' && Array.isArray(a.travelDates) ? a.travelDates : [];
    let g = crewGroups.find(g => g.name === name && g.isAdmin === isAdmin);
    if (!g) {
      g = {
        name, isAdmin,
        confirmationStatus: (typeof a === 'object' && !isAdmin) ? a.confirmationStatus : null,
        declineReason: typeof a === 'object' ? a.declineReason : null,
        periods: [],
      };
      crewGroups.push(g);
    }
    if (dateFrom || dateTo || role) g.periods.push({ dateFrom, dateTo, role, travelDates });
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>

      {/* Nav */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span onClick={onBack} style={{ color: 'var(--text-dim)', fontSize: '13px', cursor: 'pointer' }}>← Projects</span>
          <span style={{ color: 'var(--border-strong)' }}>|</span>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '16px' }}>{project.name}</span>
          <StatusBadge status={project.status} />
          <PaymentStatusBadges statuses={project.paymentStatus} />
          {pendingCnt > 0 && (
            <span style={{ background: 'var(--accent)', color: 'var(--on-accent)', borderRadius: '10px', padding: '1px 8px', fontSize: '11px', fontWeight: 'bold' }}>
              {pendingCnt} task{pendingCnt > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button onClick={onEdit} style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '8px 20px', borderRadius: '6px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
          Edit Project
        </button>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 24px', display: 'grid', gridTemplateColumns: '1fr 340px', gap: '16px', alignItems: 'start' }}>

        {/* LEFT */}
        <div>
          <SectionCard title="Timeline">
            <Timeline project={project} />
          </SectionCard>

          <SectionCard title="Finance — Full Breakdown">
            <FinanceBreakdown project={project} />
          </SectionCard>

          <SectionCard title={`Tasks${pendingCnt > 0 ? ` · ${pendingCnt} pending` : ''}`}>
            <Tasks project={project} allUsers={allUsers} />
          </SectionCard>

          <SectionCard title={`Documents${countDocs(project.docFolders) ? ` · ${countDocs(project.docFolders)}` : ''}`}>
            {(countDocs(project.docFolders) > 0 || hasCrewSubfolders(project.docFolders)) ? (
              <DocsTree folders={project.docFolders} projectId={project.id} />
            ) : (
              <p style={{ color: 'var(--text-disabled)', fontSize: '13px', margin: 0 }}>
                Немає документів. Додай їх через Edit Project → Documents.
              </p>
            )}
          </SectionCard>

          <SectionCard title="Comments">
            <Comments project={project} />
          </SectionCard>

          <SectionCard title="Activity Log">
            <ActivityLog project={project} />
          </SectionCard>
        </div>

        {/* RIGHT */}
        <div>
          <SectionCard title="General">
            <InfoRow label="Company"    value={project.company} />
            <InfoRow label="Type"       value={project.type} />
            <InfoRow label="Location"   value={project.location} />
            <InfoRow label="Start Date" value={project.startDate} />
            <InfoRow label="End Date"   value={project.endDate} />
            <InfoRow label="Created by" value={project.createdByName} />
            {project.description && (
              <div style={{ marginTop: '12px' }}>
                <p style={{ color: 'var(--text-dim)', fontSize: '11px', margin: '0 0 6px 0' }}>Description</p>
                <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>{project.description}</p>
              </div>
            )}
            {project.descriptionForFreelancers && (
              <div style={{ marginTop: '12px' }}>
                <p style={{ color: 'var(--text-dim)', fontSize: '11px', margin: '0 0 6px 0' }}>Description for freelancers</p>
                <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>{project.descriptionForFreelancers}</p>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Client's Responsible Persons">
            <ResponsiblePersonsView persons={project.persons} />
          </SectionCard>

          <SectionCard title="Finance">
            <InfoRow label="Currency" value={project.currency || 'EUR'} />
            <InfoRow label="Income"  value={project.income  ? `${project.currency || 'EUR'} ${project.income.toLocaleString()}`  : null} color="var(--success-text)" />
            <InfoRow label="Outcome" value={project.outcome ? `${project.currency || 'EUR'} ${project.outcome.toLocaleString()}` : null} color="var(--accent)" />
            <InfoRow label="Profit"  value={(project.income || project.outcome) ? `${project.currency || 'EUR'} ${(project.profit ?? (project.income - project.outcome)).toLocaleString()}` : null} color={(project.profit ?? 0) >= 0 ? 'var(--success-text)' : 'var(--accent)'} />
          </SectionCard>

          <SectionCard title="Crew">
            {crewGroups.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {crewGroups.map((g, i) => {
                  const cfg = CONFIRMATION_CONFIG[g.confirmationStatus] || null;
                  return (
                    <div key={`${g.name}-${i}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ background: g.isAdmin ? 'var(--info-bg)' : 'var(--bg-subtle)', border: `1px solid ${g.isAdmin ? 'var(--info-text)' : 'var(--border-strong)'}`, color: g.isAdmin ? 'var(--info-text)' : 'var(--text-body)', borderRadius: '20px', padding: '4px 12px', fontSize: '12px' }}>
                          {g.isAdmin ? '🛡' : '👤'} {g.name}
                        </span>
                        {g.periods.length > 1 && (
                          <span style={{ background: 'var(--bg-subtle)', color: 'var(--text-dim)', borderRadius: '10px', padding: '1px 8px', fontSize: '10px' }}>
                            {g.periods.length} periods
                          </span>
                        )}
                        {cfg && (
                          <span title={g.confirmationStatus === 'declined' ? (g.declineReason || '') : ''}
                            style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`, borderRadius: '4px', padding: '2px 8px', fontSize: '10px', fontWeight: 'bold' }}>
                            {cfg.label}
                          </span>
                        )}
                        {g.confirmationStatus === 'declined' && g.declineReason && (
                          <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>— {g.declineReason}</span>
                        )}
                      </div>
                      {g.periods.map((p, j) => {
                        const travelCount = Array.isArray(p.travelDates) ? p.travelDates.length : 0;
                        return (
                          <div key={j} style={{ color: 'var(--text-disabled)', fontSize: '11px', marginTop: '4px', marginLeft: '4px' }}>
                            📅 {p.dateFrom || '?'} → {p.dateTo || '?'}
                            {' '}<span style={{ fontStyle: 'italic' }}>
                              {travelCount > 0 ? `(✈️ ${travelCount} travel day${travelCount > 1 ? 's' : ''})` : '(no travel days marked)'}
                            </span>
                            {p.role && <span style={{ color: 'var(--text-muted)' }}> · 🎭 {p.role}</span>}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ color: 'var(--text-disabled)', fontSize: '13px', margin: 0 }}>No crew assigned</p>
            )}
          </SectionCard>

          {project.type === 'Dry hire' && (
            <SectionCard title="Equipment">
              <EquipmentView items={project.equipment} />
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectDetail;
